import { detectStudentColumns, getStudentClassInfo, isPrimaryValue, extractYearFromClass } from './classNameProcessor'

/**
 * Analyzes student data for consistency and potential issues
 * @param {Array} studentsData - Array of student data objects
 * @returns {Object} - Analysis results and categorized students
 */
export const analyzeStudentData = (studentsData) => {
    if (!studentsData || !studentsData.length) {
        return {
            categorized: {},
            summary: {},
            issues: [],
        }
    }

    // Initialize result objects
    const categorized = {
        validRecords: [],
        primaryStudents: [],
        secondaryStudents: [],
        duplicateEmails: [],
        duplicateIds: [],
        missingClass: [],
        missingNames: [],
        missingIds: [],
        missingEmails: [],
        missingPasswords: [],
        invalidGender: [],
    }

    const issues = []
    const classes = new Set()
    const years = new Set()
    const primaryClasses = new Set()
    const secondaryClasses = new Set()
    const emails = new Map()
    const studentIds = new Map()

    // Find important columns
    const findColumn = (pattern) => {
        if (!studentsData || !studentsData.length) return null

        const columns = Object.keys(studentsData[0])
        for (const column of columns) {
            const lowerColumn = column.toLowerCase()
            if (lowerColumn.includes(pattern)) {
                return column
            }
        }

        return null
    }

    // Find key columns (class / year / early access detection is shared with the upload processor)
    const { classColumn, yearColumn, earlyAccessColumn } = detectStudentColumns(studentsData)
    const emailColumn = findColumn('username') || findColumn('email') || findColumn('mail') || findColumn('user name')
    const idColumn = findColumn('id') || findColumn('number')
    const firstNameColumn = findColumn('first')
    const lastNameColumn = findColumn('last')
    const fullNameColumn = findColumn('full name') || findColumn('name')
    const genderColumn = findColumn('gender') || findColumn('sex')
    const passwordColumn = findColumn('password') || findColumn('pwd') || findColumn('pass')

    // Get student name
    const getStudentName = (student) => {
        if (firstNameColumn && lastNameColumn && student[firstNameColumn] && student[lastNameColumn]) {
            return `${student[firstNameColumn]} ${student[lastNameColumn]}`
        }

        if (fullNameColumn && student[fullNameColumn]) {
            return student[fullNameColumn]
        }

        // Try to find any field that might contain a name
        for (const key of Object.keys(student)) {
            if (key.toLowerCase().includes('name') && student[key]) {
                return student[key]
            }
        }

        return 'Unknown Student'
    }

    // Get student ID
    const getStudentId = (student) => {
        if (!idColumn) return ''
        return student[idColumn] || ''
    }

    // Check if student has primary access
    const isPrimaryStudent = (student) => {
        if (student.primary === true) return true
        // If there's no early access column, treat all students as secondary by default
        if (!earlyAccessColumn) return false
        return isPrimaryValue(student[earlyAccessColumn])
    }

    // Sheet-level warnings about class / year mapping. Without a year the backend cannot create classes.
    if (!classColumn) {
        issues.push({
            type: 'warning',
            message: 'No class column could be identified in the student sheet (expected a header such as Class, Class Name, Form, Section or Group)',
        })
    }
    if (!yearColumn) {
        issues.push({
            type: 'warning',
            message:
                'No year column could be identified in the student sheet (expected a header such as Year, Year Group, Grade or Level). Classes cannot be created without a year',
        })
    }

    // Process each student
    studentsData.forEach((student, index) => {
        const studentName = getStudentName(student)
        const studentId = getStudentId(student)
        const isEarlyAccess = isPrimaryStudent(student)
        let hasIssue = false

        // Check class/year assignments
        const classInfo = getStudentClassInfo(student, { classColumn, yearColumn })
        const className = classInfo.className
        if (className) {
            classes.add(className)

            // Track primary vs secondary classes
            if (isEarlyAccess) {
                primaryClasses.add(className)
            } else {
                secondaryClasses.add(className)
            }

            if (classInfo.year) {
                years.add(classInfo.year)
            } else {
                // Try to extract year from class name
                const extractedYear = extractYearFromClass(className).year
                if (extractedYear !== null && extractedYear !== undefined) {
                    years.add(String(extractedYear))
                } else if (yearColumn) {
                    issues.push({
                        type: 'warning',
                        message: `Student ${studentName} (${studentId}) has class ${className} but no year; the class cannot be created without a year`,
                        student,
                        index,
                    })
                }
            }
        } else {
            categorized.missingClass.push({ ...student, studentIndex: index, isPrimary: isEarlyAccess })
            issues.push({
                type: 'error',
                message: `Student ${studentName} (${studentId}) has no class assignment`,
                student,
                index,
            })
            hasIssue = true
        }

        // Check for missing names
        if ((!firstNameColumn || !student[firstNameColumn] || !lastNameColumn || !student[lastNameColumn]) && (!fullNameColumn || !student[fullNameColumn])) {
            categorized.missingNames.push({ ...student, studentIndex: index, isPrimary: isEarlyAccess })
            issues.push({
                type: 'error',
                message: `Student name is missing in row ${index + 1} ${studentId ? `with ID ${studentId}` : ''}`,
                student,
                index,
            })
            hasIssue = true
        }

        // Check for missing IDs - ALWAYS REQUIRED for both primary and secondary students
        if (!idColumn || !student[idColumn] || !student[idColumn].toString().trim()) {
            categorized.missingIds.push({ ...student, studentIndex: index, isPrimary: isEarlyAccess })
            issues.push({
                type: 'error',
                message: `Student ${studentName} has no ID`,
                student,
                index,
            })
            hasIssue = true
        } else {
            // Check for duplicate IDs
            // console.log('idColumn', idColumn)

            const id = student[idColumn].toString().trim()
            if (studentIds.has(id)) {
                const existingIndex = studentIds.get(id)
                const existingStudent = studentsData[existingIndex]

                categorized.duplicateIds.push({ ...student, studentIndex: index, duplicateWith: existingIndex, isPrimary: isEarlyAccess })
                // console.log(`DUPLICATED ID: ${id} - ${getStudentName(existingStudent)} and ${studentName}`)
                // console.log('IDS', studentIds)
                // console.log('Student', student)

                if (!issues.some((issue) => issue.type === 'error' && issue.message.includes(`Duplicate student ID: ${id}`))) {
                    issues.push({
                        type: 'error',
                        message: `Duplicate student ID: ${id} - Used by ${getStudentName(existingStudent)} and ${studentName}`,
                        student,
                        index,
                        duplicateWith: existingIndex,
                    })
                }
                hasIssue = true
            } else {
                studentIds.set(id, index)
            }
        }

        // Check email addresses - ONLY REQUIRED for secondary students
        if (emailColumn && student[emailColumn]) {
            const email = student[emailColumn].toString().trim().toLowerCase()
            if (email) {
                if (emails.has(email)) {
                    const existingIndex = emails.get(email)
                    const existingStudent = studentsData[existingIndex]

                    categorized.duplicateEmails.push({ ...student, studentIndex: index, duplicateWith: existingIndex, isPrimary: isEarlyAccess })

                    if (!issues.some((issue) => issue.type === 'error' && issue.message.includes(`Duplicate email: ${email}`))) {
                        issues.push({
                            type: 'error',
                            message: `Duplicate email: ${email} - Used by ${getStudentName(existingStudent)} and ${studentName}`,
                            student,
                            index,
                            duplicateWith: existingIndex,
                        })
                    }
                    hasIssue = true
                } else {
                    emails.set(email, index)
                }

                // Validate email format
                if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                    issues.push({
                        type: 'warning',
                        message: `Invalid email format for ${studentName}: ${email}`,
                        student,
                        index,
                    })
                    // hasIssue = true
                }
            }
        } else if (!isEarlyAccess) {
            // Email is required for secondary students only
            categorized.missingEmails.push({ ...student, studentIndex: index, isPrimary: isEarlyAccess })
            issues.push({
                type: 'error',
                message: `Secondary student ${studentName} (${studentId}) requires an email address`,
                student,
                index,
            })
            hasIssue = true
        }

        // Check gender values
        if (genderColumn) {
            if (!student[genderColumn]) {
                categorized.invalidGender.push({ ...student, studentIndex: index, isPrimary: isEarlyAccess })
                issues.push({
                    type: 'warning',
                    message: `Student ${studentName} (${studentId}) is missing gender information`,
                    student,
                    index,
                })
                hasIssue = true
            } else {
                const gender = student[genderColumn].toString().trim().toLowerCase()
                const validGenders = ['male', 'm', 'female', 'f']

                if (!validGenders.some((valid) => gender === valid || gender.includes(valid))) {
                    categorized.invalidGender.push({ ...student, studentIndex: index, isPrimary: isEarlyAccess })
                    issues.push({
                        type: 'warning',
                        message: `Student ${studentName} (${studentId}) has potentially invalid gender value: ${student[genderColumn]}`,
                        student,
                        index,
                    })
                    hasIssue = true
                }
            }
        }

        // Check for missing passwords - ONLY for secondary students when password column exists and setting is enabled

        // if (passwordColumn && !isEarlyAccess) {
        //     if (!student[passwordColumn] || String(student[passwordColumn]).trim() === '') {
        //         categorized.missingPasswords.push({ ...student, studentIndex: index, isPrimary: isEarlyAccess })
        //         issues.push({
        //             type: 'warning',
        //             message: `Secondary student ${studentName} (${studentId}) has no password set. It will be auto-generated.`,
        //             student,
        //             index,
        //         })
        //     }
        // }

        // Categorize by primary/secondary status
        if (isEarlyAccess) {
            categorized.primaryStudents.push({ ...student, studentIndex: index, isPrimary: true })
        } else {
            categorized.secondaryStudents.push({ ...student, studentIndex: index, isPrimary: false })
        }

        // Add to Valid Entries if no issues found
        if (!hasIssue) {
            categorized.validRecords.push({ ...student, studentIndex: index, isPrimary: isEarlyAccess })
        } else {
            // console.log(studentName, 'has issues:', hasIssue, issues)
        }
    })

    // Generate summary
    const summary = {
        total: studentsData.length,
        primaryStudents: categorized.primaryStudents.length,
        secondaryStudents: categorized.secondaryStudents.length,
        uniqueClasses: classes.size,
        primaryClasses: primaryClasses.size,
        secondaryClasses: secondaryClasses.size,
        uniqueYears: years.size,
        validRecords: categorized.validRecords.length,
        missingClass: categorized.missingClass.length,
        duplicateEmails: categorized.duplicateEmails.length,
        duplicateIds: categorized.duplicateIds.length,
        missingNames: categorized.missingNames.length,
        missingIds: categorized.missingIds.length,
        invalidGender: categorized.invalidGender.length,
        missingPasswords: categorized.missingPasswords.length,
        missingEmails: categorized.missingEmails.length,
        issuesCount: issues.length,
        classColumn,
        emailColumn,
        idColumn,
        nameColumns: {
            firstName: firstNameColumn,
            lastName: lastNameColumn,
            fullName: fullNameColumn,
        },
        genderColumn,
        earlyAccessColumn,
        passwordColumn,
    }

    return {
        categorized,
        summary,
        issues,
        columns: {
            classColumn,
            yearColumn,
            emailColumn,
            idColumn,
            firstNameColumn,
            lastNameColumn,
            fullNameColumn,
            genderColumn,
            earlyAccessColumn,
            passwordColumn,
        },
    }
}
